import { tracked } from '@glimmer/tracking';

import { BlockVisualization, DataSelectType, OrderDirection } from 'webapp/enums/enums';
import { equal } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import type { DataSelectProps } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

export interface NumberPointAndChartOutputPair {
	numberPoint: DChartPrimaryPoint;
	chartOutput?: DsDataOutputTopLevel;
}

const RecordLimit = 15;

export default class VisualizationNumber extends VisualizationBase {
	visualizationType = BlockVisualization.NUMBER;

	recordLimit = RecordLimit;

	// ------- visualization specifics --------

	get hasSmallChart(): boolean {
		const firstDataSelect = this.blockSetup.firstShownDataSelect;
		return !!firstDataSelect && !!this.getChartForNumberDs(firstDataSelect);
	}

	set hasSmallChart(hasChart: boolean) {
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			const chartDs = this.getChartForNumberDs(ds);
			if (chartDs) {
				if (!hasChart) {
					this.blockSetup.deleteDataSelect(chartDs);
				}
			} else {
				if (hasChart) {
					this.createSmallChartDataSelect(ds);
				}
			}
		});
	}

	private createSmallChartDataSelect(forDataSelect: DataSelect): void {
		const limitationsCopy = forDataSelect.limitations.createCopy();
		limitationsCopy.isNumber = false;
		limitationsCopy.selectOptimalValidGranulation();

		const metricSetupData = forDataSelect.metricSetupData;

		const props: DataSelectProps = {
			type: DataSelectType.MAIN,
			updateFrom: forDataSelect.numericId,
			dateRange: limitationsCopy.dateRange?.formula,
			metricSetupKey: metricSetupData?.key,
			metricSetupSpaceAccessId: metricSetupData?.spaceAccessId,
			metricSetupDimensions: metricSetupData?.dimensions,
			metricSetupFilters: metricSetupData?.filters,
		};

		this.blockSetup.createDataSelect(props);
	}

	// ------- data for display --------

	@tracked numberPointAndChartOutputPairs: NumberPointAndChartOutputPair[] = [];

	override updateDataCache() {
		this.numberPointAndChartOutputPairs = this.getNumberPointAndChartOutputPairs();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.numberPointAndChartOutputPairs = [];
	}

	updateColorIndexes() {
		// on numbers only
		const points = this.numberPointAndChartOutputPairs.map((p) => p.numberPoint);
		this.updateColorIndexesForItems(points);
	}

	private getNumberPointAndChartOutputPairs(): NumberPointAndChartOutputPair[] {
		const pairs = this.getNumberPointAndChartOutputPairsUnsorted();
		const sortField = this.block.sortField;
		pairs.sort((a, b) => {
			return DChartPrimaryPoint.doPointCompare(
				a.numberPoint,
				b.numberPoint,
				sortField,
				this.block.sortOrder || OrderDirection.DESC,
			);
		});
		return pairs;
	}

	private getNumberPointAndChartOutputPairsUnsorted(): NumberPointAndChartOutputPair[] {
		type NumberPointAndOutputAndChartOutputPair = NumberPointAndChartOutputPair & {
			number: DsDataOutputTopLevel;
		};
		const pairs: NumberPointAndOutputAndChartOutputPair[] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((numberDs) => {
			if (numberDs.dataRoot) {
				const chartDs = this.getChartForNumberDs(numberDs),
					chartOutputs = chartDs?.dataRoot?.outputs || [];

				numberDs.dataRoot.outputs.forEach((numberOutput) => {
					const points = this.createDChartPointsFromOutput(numberOutput);
					const pair: NumberPointAndOutputAndChartOutputPair = {
						number: numberOutput,
						numberPoint: points[0]!,
					};
					const chartOutputForThisNumber = chartOutputs.find((chartOutput) => {
						return (
							equal(
								chartOutput.dimensionValues,
								numberOutput.dimensionValues,
								true,
							) && chartOutput.unit === numberOutput.unit
						);
					});
					if (chartOutputForThisNumber) {
						pair.chartOutput = chartOutputForThisNumber;
					}
					pairs.push(pair);
				});
			}
		});

		return pairs.map((p) => {
			return {
				numberPoint: p.numberPoint,
				chartOutput: p.chartOutput,
			};
		});
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block,
			dss = this.blockSetup.dataSelects;
		return hashCode(
			JSON.stringify({
				sf: block.sortField,
				so: block.sortOrder,
				bc: block.colors,
				o: dss.map((ds) => ds.order), // bcos of possible custom sort
			}),
		);
	}

	// ------- general helpers --------

	private getChartForNumberDs(dataSelect: DataSelect): DataSelect | null {
		const chartDs = this.blockSetup
			.getSecondaryDataSelectsForDataSelect(dataSelect)
			.find((item) => item.type === DataSelectType.MAIN);
		return chartDs || null;
	}

	override getTypeTextForDataSelect(dataSelect: DataSelect): string {
		return dataSelect.isPrimary ? 'number' : 'chart';
	}

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// if this.hasSmallChart, also add series DS, but only after it's saved
		if (this.hasSmallChart) {
			void dataSelect.saveAndCatch().then(() => {
				if (dataSelect.id) {
					this.createSmallChartDataSelect(dataSelect);
				}
			});
		}
	}

	// ------- visualization switch helpers --------

	override getExcessDataSelectsForSwitchTo(_to: VisualizationBase): DataSelect[] {
		// getting rid of all chart DS
		return this.blockSetup.secondaryDataSelects;
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		this.numberPointAndChartOutputPairs.forEach((pair) => {
			points.push(pair.numberPoint);
		});

		return points;
	}

	static override recordLimit = RecordLimit;
}
